import { Avatar, Box, Collapse, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useUpdateNote } from '../../services/react-query/notes';
import { queryClient } from '../../services/react-query/queryClient';
import { queryKeys } from '../../services/react-query/queryKeys';
import { useGetCurrentUser } from '../../services/react-query/users/useGetCurrentUser';
import { Note } from '../../types/Note';
import { groupNotesByParent } from '../../utility/notes/groupNotesByParent';
import { childNoteStyles, noteReplyAvatarGroupStyles, noteReplyCountStyles, noteReplyToggleStyles, parentNoteStyles } from './noteStyles';
import NoteSummary from './NoteSummary';

interface INotesTableProps {
  notes: Note[];
  handleNoteEdit: (note: Note) => void;
  handleReply: (parentUuid: string) => void;
  expandedNoteUuids: string[];
  setExpandedNoteUuids: React.Dispatch<React.SetStateAction<string[]>>;
}

const NotesList: React.FC<INotesTableProps> = ({ notes, handleNoteEdit, handleReply, expandedNoteUuids, setExpandedNoteUuids }) => {
  const { data: currentUser } = useGetCurrentUser();
  const currentUserEmail = currentUser?.email;
  const [uuid, setUuid] = useState('');

  const handlePinUpdate = (note: Note) => {
    const isPinned = !!note.pinned_at;
    const newPinnedAt = note.pinned_at ? null : new Date().toISOString();
    setUuid(_.toString(note?.uuid));
    updateNote({
      ...note,
      pinned_at: newPinnedAt,
      pin: !isPinned,
    });
  };

  const saveCallback = (updatedNote: Note) => {
    queryClient.invalidateQueries({
      queryKey: [
        queryKeys.allNotes,
        {
          filters: {
            noteable_uuid: updatedNote.noteable_uuid,
            noteable_type: updatedNote.noteable_type,
          },
        },
      ],
    });
    if (uuid === 'new') {
      queryClient.invalidateQueries({ queryKey: [queryKeys.allNotes, uuid] });
    }
  };

  const { mutate: updateNote } = useUpdateNote({
    uuid,
    onSuccessCallback: saveCallback,
  });

  const toggleExpand = (uuid: string) => {
    setExpandedNoteUuids((prev) => (prev.includes(uuid) ? prev.filter((id) => id !== uuid) : [...prev, uuid]));
  };

  const groupedNotes = useMemo(() => groupNotesByParent(notes), [notes]);

  return groupedNotes.map((note) => {
    const isExpanded = expandedNoteUuids.includes(note.uuid!);
    const hasChildren = note.children && note.children.length > 0;

    // Prepare recent unique replies for avatar display
    const recentReplies = _(note.children).slice().reverse().uniqBy('user_email').take(3).reverse().value();

    return (
      <Box key={note.uuid} sx={parentNoteStyles}>
        <NoteSummary
          note={note}
          currentUserEmail={currentUserEmail}
          onEdit={() => handleNoteEdit(note)}
          onPin={() => handlePinUpdate(note)}
          onReply={() => handleReply(note.uuid!)}
        />

        {hasChildren && (
          <Box sx={{ pl: 2 }}>
            {!isExpanded && (
              <Box sx={noteReplyToggleStyles} onClick={() => toggleExpand(note.uuid!)}>
                {recentReplies.length > 0 && (
                  <Box sx={noteReplyAvatarGroupStyles}>
                    {recentReplies.map((child) => (
                      <Avatar key={child.uuid} src={child.employee_avatar_url} alt={child.employee_display_name} sx={{ width: 24, height: 24 }} />
                    ))}
                  </Box>
                )}
                <Typography variant="body2" color="primary" sx={noteReplyCountStyles}>
                  {_.size(note.children)} {_.size(note.children) > 1 ? 'replies' : 'reply'}
                </Typography>
              </Box>
            )}

            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box>
                {_.map(note.children, (child) => (
                  <Box key={child.uuid} sx={childNoteStyles}>
                    <NoteSummary note={child} currentUserEmail={currentUserEmail} onEdit={() => handleNoteEdit(child)} />
                  </Box>
                ))}

                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ cursor: 'pointer', minHeight: '24px', textDecoration: 'underline', fontWeight: 'bold', mt: 1 }}
                  onClick={() => toggleExpand(note.uuid!)}
                >
                  Hide {_.size(note.children) > 1 ? 'replies' : 'reply'}
                </Typography>
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    );
  });
};

export default NotesList;
